import { useState, useRef, useEffect } from "react";
import type { Lead, Priority } from "@/mocks/leadsData";
import { sourceBadgeColors, stageBadgeColors, priorityDotColors, getScoreBadgeColors, getScoreLabel } from "@/mocks/leadsData";

type Props = {
  leads: Lead[];
  onView: (lead: Lead) => void;
  onEdit: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
};

export default function LeadsCardView({ leads, onView, onEdit, onDelete }: Props) {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const getInitials = (name?: string) =>
    name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
      : "U";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" ref={menuRef}>
      {leads.map((lead) => (
        <div
          key={lead.id}
          className="bg-white rounded-xl border border-gray-200 p-4 hover:border-indigo-300 transition-all duration-200 cursor-pointer relative"
          onMouseEnter={() => setHoveredId(lead.id)}
          onMouseLeave={() => { setHoveredId(null); setOpenMenuId(null); }}
          onClick={() => onView(lead)}
        >
          {/* Top row: avatar + priority dot */}
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-700 flex-shrink-0">
              {getInitials(lead.name)}
            </div>
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${priorityDotColors[lead.priority as Priority] || "bg-gray-400"}`}
            />
          </div>

          {/* Name + Company */}
          <p className="font-semibold text-gray-900 text-sm mt-2.5 truncate">{lead.name}</p>
          <p className="text-xs text-gray-500 truncate">{lead.company}</p>

          {/* Badges row */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${sourceBadgeColors[lead.source] || "bg-indigo-50 text-indigo-700"}`}>
              {lead.sourceAccount ? `${lead.source} • ${lead.sourceAccount}` : lead.source}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${stageBadgeColors[lead.stage] || "bg-gray-100 text-gray-700"}`}>
              {lead.stage}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getScoreBadgeColors(lead.score || 50)}`}>
              {lead.score ?? 50} · {getScoreLabel(lead.score || 50)}
            </span>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 mt-3 pt-3 flex items-center justify-between">
            <span className="text-xs text-gray-500 flex items-center gap-1 whitespace-nowrap">
              <i className="ri-phone-line text-[10px]" /> {lead.phone}
            </span>
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-[9px] font-bold text-indigo-700 flex-shrink-0">
                {getInitials(lead.assigned)}
              </div>
              <span className="text-xs text-gray-500 truncate max-w-[80px]">{lead.assigned || "Unassigned"}</span>
            </div>
          </div>

          {/* Hover action buttons */}
          {hoveredId === lead.id && (
            <div
              className="absolute bottom-3 right-3 flex items-center gap-1 bg-white/95 rounded-lg px-1.5 py-1 border border-gray-100 shadow-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => onView(lead)}
                className="p-1.5 rounded-md hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors"
                title="View"
              >
                <i className="ri-eye-line text-sm" />
              </button>
              <button
                onClick={() => onEdit(lead)}
                className="p-1.5 rounded-md hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors"
                title="Edit"
              >
                <i className="ri-edit-line text-sm" />
              </button>
              <div className="relative">
                <button
                  onClick={() => setOpenMenuId(openMenuId === lead.id ? null : lead.id)}
                  className="p-1.5 rounded-md hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors"
                  title="More"
                >
                  <i className="ri-more-2-fill text-sm" />
                </button>
                {openMenuId === lead.id && (
                  <div className="absolute right-0 bottom-full mb-1 w-36 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-20">
                    <button
                      onClick={() => { onEdit(lead); setOpenMenuId(null); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-edit-line text-gray-400" /> Edit
                    </button>
                    <hr className="my-0.5 border-gray-100" />
                    <button
                      onClick={() => { onDelete(lead); setOpenMenuId(null); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line" /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
      {leads.length === 0 && (
        <div className="col-span-full py-16 text-center">
          <i className="ri-user-search-line text-3xl text-gray-300 mb-2 block" />
          <p className="text-sm text-gray-500">No leads found</p>
        </div>
      )}
    </div>
  );
}